import { axios } from '../../../config/connection/axios';

/** Uma mudança num relatório que já tinha compra/lote: o que mudou, onde e por quem. Ver `AvisoMudanca` no back. */
export interface IAviso {
    _id: string;
    porNome: string;
    header: string;
    tituloRelatorio: string;
    tipo: 'compra' | 'lote';
    pedido?: string;
    lote?: string;
    alvoNome: string;
    situacao: string;
    itens: { codigo: number; descricao: string; acao: 'adicionado' | 'excluido' | 'restaurado'; detalhe: string }[];
    vistoEm: string | null;
    createdAt: string;
}

export const ACAO_DO_AVISO: Record<string, string> = {
    adicionado: 'Adicionado',
    excluido: 'Excluído',
    restaurado: 'Restaurado',
};

/** Os avisos não vistos de quem está logado. */
export const meusAvisos = (): Promise<IAviso[]> => axios.get('/avisos');

/** Os avisos de quem está logado sobre um relatório, uma compra ou um lote (os vistos há até 7 dias também). */
export const avisosDe = (tipo: 'relatorio' | 'compra' | 'lote', id: string): Promise<IAviso[]> => axios.get(`/avisos/${tipo}/${id}`);

/** Histórico: todas as notificações dos últimos 60 dias, vistas ou não. */
export const historicoDeAvisos = (): Promise<IAviso[]> => axios.get('/avisos/historico');

export const marcarVisto = (id: string) => axios.put(`/avisos/${id}/visto`);
export const marcarTodosVistos = () => axios.put('/avisos/vistos');

/** Para onde o aviso leva: a compra (já no produto), ou a página de lotes já no relatório. */
export const rotaDoAlvo = (a: IAviso, codigo?: number) => (a.tipo === 'compra' && a.pedido
    ? `/app/compras/${a.pedido}${codigo ? `?produto=${codigo}` : ''}`
    : `/app/remarcacoes?r=${a.header}`);
/** O relatório, já aberto no produto. */
export const rotaDoRelatorioDoAviso = (a: IAviso, codigo?: number) =>
    `${a.tipo === 'lote' ? '/app/report-rem' : '/app/report'}?r=${a.header}${codigo ? `&produto=${codigo}` : ''}`;

/** O que a pessoa precisa fazer, conforme o que mudou e a situação da compra/do lote. */
export function oQueFazer(a: IAviso) {
    const acoes = new Set<string>(a.itens.map(i => i.acao));
    const so = (x: string) => acoes.size === 1 && acoes.has(x);
    if (a.tipo === 'lote') {
        if (a.situacao === 'Efetivado') {
            if (so('excluido')) return 'O lote já estava efetivado: o produto continua remarcado no Vetor. Se não era para remarcar, desfaça lá.';
            return 'O lote já estava efetivado: o produto precisa ser remarcado no Vetor (a data de remarcação dele não foi gravada).';
        }
        // 'Digitado no Vetor' e 'Gerado' são os textos dos avisos de antes de 19/10/2026.
        if (a.situacao === 'Digitado no Vetor' || a.situacao === 'Digitado' || a.situacao === 'Digitando') {
            const ja = a.situacao === 'Digitando' ? 'O app já estava digitando este departamento no Mitryus' : 'O lote já estava digitado no Mitryus';
            if (so('excluido')) return `${ja}: retire o produto do lote lá também.`;
            if (so('adicionado') || so('restaurado')) return `${ja}: inclua o produto no lote lá também.`;
            return `${ja}: inclua/retire os produtos lá também.`;
        }
        return 'O departamento ainda não foi digitado: a mudança já vale para a digitação. Se o arquivo já foi baixado, baixe de novo.';
    }
    if (a.situacao === 'Concluída') {
        if (so('excluido')) return 'A compra já estava concluída: confira com o assessor de compras se o produto ainda pode ser cancelado no fornecedor.';
        return 'A compra já estava concluída: confira com o assessor de compras se o produto ainda pode entrar.';
    }
    if (so('excluido')) return 'O produto saiu da compra: aparece em vermelho e não conta mais nos totais. Não precisa ser comprado.';
    if (so('restaurado')) return 'O produto voltou para a compra: o assessor de compras e o lojista podem trabalhar nele de novo.';
    if (so('adicionado')) return 'O produto entrou na compra: o assessor de compras e o lojista já podem trabalhar nele.';
    return 'A compra foi atualizada com essas mudanças.';
}
